/**
 * The four parties a pathology vendor named on the payload a result came from, and everything
 * the client is allowed to do with them.
 *
 * Mirrors the server column of the same name — see `services/lancetIntegrationService`
 * (`buildVendorDoctors`) and `models/patient-lab-order.js`. BOTH list endpoints serve it:
 *
 *   GET /api/results/group/:groupId            -> per row: `vendorDoctors`
 *   GET /api/results/group/:groupId/unlinked   -> per row: `vendorDoctors`
 *
 * and BOTH put `vendorDoctorPlatformCode` on the ENVELOPE, not on the row.
 *
 * ── THE THREE THINGS THAT ARE EASY TO GET WRONG ──────────────────────────────────────
 *
 * 1. `vendorDoctors` IS NULL ON EVERY PRE-EXISTING ROW. There is no backfill and there never
 *    will be: only rows captured through the vendor path after the column shipped carry it.
 *    At the time of writing that is 0 of 337 lab orders and 0 of 8 unlinked results. Rendering
 *    nothing at all for that state is the DEFAULT case, not an edge case — no empty container,
 *    no dangling slot label, no reserved space.
 *
 * 2. AN ABSENT SLOT IS `null`, NEVER `{}` AND NEVER MISSING. All four keys are always present
 *    on a non-null object. A `{}` slot is not a party the lab named; it is a bug, and it must
 *    not become a badge.
 *
 * 3. THE PLATFORM CODE IS OUR OWN CODE ARRIVING AS A PARTY. `00MEDICAI` (role `COMP`) is on
 *    nearly every payload that came through this platform and is noise on a badge set. The
 *    server stores it faithfully — the column is a copy of the payload, not a summary of it —
 *    and tells the client which code to drop at render time. It is READ FROM THE ENVELOPE.
 *    Never hard-code it here: the day Lancet reissues it, a constant in the client is a lie
 *    that suppresses the wrong party or stops suppressing the right one.
 *
 * ── NO DISPLAY-NAME MAPPING ──────────────────────────────────────────────────────────
 *
 * `role` is rendered RAW — `UROL`, `HOSP`, `NEURSURG`, `WARD`. There is deliberately no lookup
 * table: the live vocabulary was 26 values when this was written and it grows whenever the lab
 * adds a discipline. A stale map renders a blank or a wrong specialty for a code it has not
 * heard of, which is worse than the code itself. The same rule is enforced on the server, where
 * an unrecognised role is stored and never rejected.
 *
 * ── THE FOURTH SLOT IS "Other" ───────────────────────────────────────────────────────
 *
 * Lancet's element is `<OtherDoctor>` and this label follows it. It is NOT "Ward". `WARD` is a
 * ROLE value that can turn up in any slot, and `OtherDoctor` frequently names a real consultant
 * (`PROFC04 / DR CHRISTOS PROFYRIS`, role `NEURSURG`). Labelling the slot "Ward" would be wrong
 * about both halves.
 */

/** One party, exactly as the lab sent it. `code` is RAW and may contain spaces (`GARD SEC1`). */
export interface VendorDoctorSlot {
  code?: string | null;
  name?: string | null;
  /** The vendor's own `type=` attribute, verbatim. May be absent on a party the lab did not type. */
  role?: string | null;
}

/**
 * All four keys are ALWAYS present when the object itself is present. An unfilled slot is
 * `null`. The object as a whole is `null` on every row that did not come through the vendor
 * capture path.
 */
export interface VendorDoctors {
  recipient: VendorDoctorSlot | null;
  submittingDoctor: VendorDoctorSlot | null;
  registeringDoctor: VendorDoctorSlot | null;
  other: VendorDoctorSlot | null;
}

export type VendorDoctorSlotKey = keyof VendorDoctors;

/** Anything that can carry the column — a lab order row or an unlinked capture row. */
export interface VendorDoctorBearing {
  vendorDoctors?: VendorDoctors | null;
}

/**
 * Slot order and the labels the product owner chose. `other` is LAST and is labelled "Other".
 * Declared once so the two surfaces cannot drift apart.
 */
export const VENDOR_DOCTOR_SLOTS: ReadonlyArray<{ key: VendorDoctorSlotKey; label: string }> = [
  { key: 'recipient', label: 'Recipient' },
  { key: 'submittingDoctor', label: 'Submitting' },
  { key: 'registeringDoctor', label: 'Registering' },
  { key: 'other', label: 'Other' }
];

/** One rendered chip. `role` is the raw code; `tooltip` carries the identity behind it. */
export interface VendorDoctorBadge {
  key: VendorDoctorSlotKey;
  label: string;
  /** The RAW role code. Never mapped, never title-cased. */
  role: string;
  code: string;
  name: string;
  tooltip: string;
}

/**
 * ONE shared empty array, returned by every "nothing to render" path.
 *
 * Identity matters: these helpers are called from `*ngFor`, and handing back a fresh `[]` on
 * every change-detection pass makes Angular tear down and rebuild the row. Frozen so a caller
 * cannot push into the shared instance.
 */
const NO_BADGES: ReadonlyArray<VendorDoctorBadge> = Object.freeze([]);

/** Trimmed, or `''`. Treats `null`, `undefined` and a whitespace-only value alike. */
function text(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * True when this slot is OUR OWN platform code wearing a party's clothes.
 *
 * Compared trimmed and case-insensitively: `code` is the raw payload attribute and the same
 * platform code has been observed padded. When the envelope carried no platform code — an older
 * server, or a response that genuinely has none — NOTHING is suppressed, because suppressing on
 * a guess would hide a real clinician.
 */
export function isVendorDoctorPlatformSlot(
  slot: VendorDoctorSlot | null | undefined,
  platformCode: string | null | undefined
): boolean {
  const wanted = text(platformCode);
  if (!wanted) {
    return false;
  }
  const code = text(slot?.code);
  return code.length > 0 && code.toUpperCase() === wanted.toUpperCase();
}

/**
 * The chips for one row, in slot order, with the platform-code slot removed.
 *
 * Returns the SHARED empty array — never a new one — when there is nothing to show, so a
 * template can test `.length` and render no container at all.
 *
 * A slot with no `role` produces NO chip. The product decision is "render the raw role code";
 * a slot the lab did not type has no role code to render, and a chip carrying only the word
 * "Recipient" is the dangling label this feature was asked not to produce. It also keeps one
 * invariant that the filter depends on: a chip exists for exactly the roles that appear as
 * filter options, so no option can ever match nothing and no chip can ever be unfilterable.
 */
export function vendorDoctorBadges(
  row: VendorDoctorBearing | null | undefined,
  platformCode: string | null | undefined
): ReadonlyArray<VendorDoctorBadge> {
  const doctors = row?.vendorDoctors;
  // `null` on every pre-existing row. This is the common branch, not the unusual one.
  if (!doctors || typeof doctors !== 'object') {
    return NO_BADGES;
  }

  const badges: VendorDoctorBadge[] = [];
  for (const slotDef of VENDOR_DOCTOR_SLOTS) {
    const slot = doctors[slotDef.key];
    // `null` for a slot the lab left empty; `{}` would be a server bug and is treated the same
    // way — an object with no role yields no chip below.
    if (!slot || typeof slot !== 'object') {
      continue;
    }
    if (isVendorDoctorPlatformSlot(slot, platformCode)) {
      continue;
    }
    const role = text(slot.role);
    if (!role) {
      continue;
    }
    const code = text(slot.code);
    const name = text(slot.name);
    const identity = [code, name].filter(part => part.length > 0).join(' — ');
    badges.push({
      key: slotDef.key,
      label: slotDef.label,
      role,
      code,
      name,
      tooltip: identity ? `${slotDef.label}: ${identity}` : slotDef.label
    });
  }

  return badges.length ? badges : NO_BADGES;
}

/** The raw role codes on one row, platform-code slots excluded. Order follows slot order. */
export function vendorDoctorRolesOf(
  row: VendorDoctorBearing | null | undefined,
  platformCode: string | null | undefined
): string[] {
  return vendorDoctorBadges(row, platformCode).map(badge => badge.role);
}

/**
 * The filter's options: every DISTINCT role actually present on the rows handed in.
 *
 * Derived from the loaded rows and NEVER from a hard-coded list — the vocabulary grows on the
 * vendor's schedule, not ours, and an option that matches nothing is worse than a missing one.
 * Pass the rows that are already visible under any OTHER filter (the text search), so a role
 * that only exists on a row the search has excluded does not appear as a choice.
 *
 * Sorted so the menu is stable between renders; empty when no loaded row has any role, which is
 * the caller's signal to render no filter control at all.
 */
export function vendorDoctorRoleOptions(
  rows: ReadonlyArray<VendorDoctorBearing | null | undefined> | null | undefined,
  platformCode: string | null | undefined
): string[] {
  if (!rows || !rows.length) {
    return [];
  }
  const seen = new Set<string>();
  for (const row of rows) {
    for (const role of vendorDoctorRolesOf(row, platformCode)) {
      seen.add(role);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * Does this row survive the role filter?
 *
 * Multi-select is OR — a row matching ANY ticked role is kept. Nothing ticked means no
 * filtering at all, so every row passes, including the rows with no `vendorDoctors`. That
 * matters: today those are all of them, and a filter control that silently empties the list the
 * moment it is opened would be unusable.
 *
 * This composes with, and never replaces, whatever other filter the surface already has. The
 * caller ANDs the two.
 */
export function matchesVendorDoctorRoles(
  row: VendorDoctorBearing | null | undefined,
  selectedRoles: ReadonlyArray<string> | null | undefined,
  platformCode: string | null | undefined
): boolean {
  if (!selectedRoles || !selectedRoles.length) {
    return true;
  }
  const roles = vendorDoctorRolesOf(row, platformCode);
  if (!roles.length) {
    return false;
  }
  return roles.some(role => selectedRoles.includes(role));
}

/**
 * Per-row badge memo.
 *
 * `*ngFor` re-evaluates a template expression on every change-detection pass. Without this, each
 * pass builds a new array for every visible row and Angular rebuilds the whole chip list —
 * visible as flicker on a 500-row Results page. Keyed on the row object itself, so it is thrown
 * away with the row; dropped wholesale when the platform code changes, because every suppression
 * decision in it was made against the old one.
 */
export class VendorDoctorBadgeCache {
  private cache = new WeakMap<object, ReadonlyArray<VendorDoctorBadge>>();
  private platformCode: string | null = null;

  badgesFor(
    row: VendorDoctorBearing | null | undefined,
    platformCode: string | null | undefined
  ): ReadonlyArray<VendorDoctorBadge> {
    const code = platformCode ?? null;
    if (code !== this.platformCode) {
      this.cache = new WeakMap<object, ReadonlyArray<VendorDoctorBadge>>();
      this.platformCode = code;
    }
    if (!row || typeof row !== 'object') {
      return NO_BADGES;
    }
    const hit = this.cache.get(row);
    if (hit) {
      return hit;
    }
    const computed = vendorDoctorBadges(row, code);
    this.cache.set(row, computed);
    return computed;
  }

  /** Drops everything. Call when the underlying rows are replaced wholesale. */
  clear(): void {
    this.cache = new WeakMap<object, ReadonlyArray<VendorDoctorBadge>>();
  }
}

/**
 * `{ success, data }` plus the envelope-level platform code, as both list endpoints send it.
 *
 * `vendorDoctorPlatformCode` is on the ENVELOPE and not on the row — there is one platform code
 * per response, and putting it on 500 rows would invite a client to trust a row's copy of it.
 */
export interface VendorDoctorEnvelope {
  vendorDoctorPlatformCode?: string | null;
}
